import { Table } from '@dixa/lib-shared-habla';
import {
  flexRender,
  type Header,
  type Table as TanstackTable,
} from '@tanstack/react-table';
import type { SearchConversationEnriched } from 'lib-search-types';
import { ampli } from 'lib-shared-tracking';

import type { ConversationsTableFeatures } from '../../../conversationsTable.columns';
import { getConversationsTableColumnId } from '../../../conversationsTable.utils';
import { ConversationsTableFillerCell } from '../../fillerCell/conversationsTableFillerCell';
import { TextPlaceholder } from '../../placeholders/textPlaceholder/textPlaceholder';

export type ConversationsTableHeadRowProps = {
  /** The table whose column headers are rendered. */
  table: TanstackTable<ConversationsTableFeatures, SearchConversationEnriched>;
  /** Whether the table has been initialized. */
  isInitialized?: boolean;
};

export const ConversationsTableHeadRow = ({
  table,
  isInitialized,
}: ConversationsTableHeadRowProps): JSX.Element => {
  const isCustomizable = Boolean(table.options.meta?.layout);

  const renderHeader = (
    header: Header<
      ConversationsTableFeatures,
      SearchConversationEnriched,
      unknown
    >,
  ) => {
    const meta = header.column.columnDef.meta;
    const width = (!isCustomizable && meta?.width) || `${header.getSize()}px`;
    const columnId = getConversationsTableColumnId(header.column);
    const isResizable = header.column.getCanResize();

    if (!isInitialized) {
      return (
        <Table.Th key={header.id} width={width} columnId={columnId}>
          {meta?.placeholder?.header && (
            <TextPlaceholder
              width={meta.placeholder.header}
              style={{
                marginLeft:
                  header.column.getIsPinned() === 'end' ? 'auto' : undefined,
              }}
            />
          )}
        </Table.Th>
      );
    }

    return (
      <Table.Th
        key={header.id}
        width={width}
        tooltip={meta?.tooltip}
        columnId={columnId}
        resizingProps={
          isResizable
            ? {
                label: `Resize ${meta?.label} column`,
                width: header.getSize(),
                minWidth: header.column.columnDef.minSize,
                isResizing: header.column.getIsResizing(),
                onResizeStart: header.getResizeHandler(),
                onResize: (nextWidth) =>
                  table.setColumnSizing((columnSizing) => ({
                    ...columnSizing,
                    [header.column.id]: nextWidth,
                  })),
                onReset: () => {
                  header.column.resetSize();
                  ampli.conversationsTableColumnWidthReset({
                    column: header.column.id,
                  });
                },
              }
            : undefined
        }
        truncate={!isCustomizable || isResizable}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </Table.Th>
    );
  };

  if (!isCustomizable) {
    return <Table.Tr>{table.getLeafHeaders().map(renderHeader)}</Table.Tr>;
  }

  return (
    <Table.Tr>
      {table.getStartLeafHeaders().map(renderHeader)}
      {table.getCenterLeafHeaders().map(renderHeader)}
      <ConversationsTableFillerCell isHeader />
      {table.getEndLeafHeaders().map(renderHeader)}
    </Table.Tr>
  );
};
